import { LAYOUT } from '@server/parser';
import { ConfigurationItem } from '../../parser/ParserTypes';

const RT_NAME_FUNNEL_ID_SEPARATOR = '?$%=';
const RT_OBJECTNAME_SEPARATOR = '.';
export const FULL_NAME_SEPARATOR = '%&?';
export const MASTER_KEY = '__MASTER__';

interface CreateRecordTypeIdentifierProps {
  recordTypeName: string;
  funnelId: string;
  objectName: string;
}

interface CreateStepIdProps {
  objectName: string;
  leadingField: string;
  stepFullName: string;
}

export const createRecordTypeIdentifier = ({
  recordTypeName,
  funnelId,
  objectName,
}: CreateRecordTypeIdentifierProps): string => {
  // If record type name is not provided, that means that funnel was not yet deployed
  // to properly support sweep elements that may use this record type we should provide funnelId
  // to properly support SF elements that may use this record type we should provide MASTER_KEY
  return `${recordTypeName ?? MASTER_KEY}${RT_NAME_FUNNEL_ID_SEPARATOR}${funnelId}${RT_OBJECTNAME_SEPARATOR}${objectName}`;
};

export const getDataFromRecordTypeIdentifier = (
  recordTypeIdenifier: string,
): {
  objectName: string;
  recordTypeName: string;
  funnelId: string | undefined;
  recordTypeNameAndFunnelId: string;
} => {
  const [recordTypeNameAndFunnelId, objectName] =
    recordTypeIdenifier.split(RT_OBJECTNAME_SEPARATOR);
  const [recordTypeName, funnelId] = recordTypeNameAndFunnelId.split(RT_NAME_FUNNEL_ID_SEPARATOR);
  return { objectName, recordTypeName, funnelId, recordTypeNameAndFunnelId };
};

export const createStepId = ({
  objectName,
  stepFullName,
  leadingField,
}: CreateStepIdProps): string => {
  return `${objectName}${FULL_NAME_SEPARATOR}${leadingField}${FULL_NAME_SEPARATOR}${stepFullName}`;
};

export const getDataFromStepId = (
  stepId: string,
): {
  objectName: string;
  leadingField: string;
  stepFullName: string;
} => {
  const [objectName, leadingField, stepFullName] = stepId.split(FULL_NAME_SEPARATOR);
  return { objectName, leadingField, stepFullName };
};

//Expecting id created with createRecordTypeIdentifier
export const isConfigurationItemUsedByRecordType = (id: string) => (rule: ConfigurationItem) => {
  const { recordTypeName, objectName, funnelId } = getDataFromRecordTypeIdentifier(id);
  const idOfFunnelLevel = createRecordTypeIdentifier({
    objectName,
    funnelId: funnelId ?? '',
    recordTypeName: '',
  });

  const idOfRecordTypeLevel = createRecordTypeIdentifier({
    objectName,
    recordTypeName,
    funnelId: '',
  });

  const isObjectLevel = !rule.usedByRecordTypes?.length && !rule.usedByLeadingFieldPicklist?.length;
  const isLayout = rule.parentType === LAYOUT;

  if (isObjectLevel && !isLayout) {
    return true;
  }

  return rule.usedByRecordTypes?.some((ruleRecordTypeIdenifier) => {
    const { recordTypeName, objectName, funnelId } =
      getDataFromRecordTypeIdentifier(ruleRecordTypeIdenifier);

    const funnelLevel = createRecordTypeIdentifier({
      objectName,
      funnelId: funnelId ?? '',
      recordTypeName: '',
    });

    const isFunnelLevelMatch = funnelId ? idOfFunnelLevel.endsWith(funnelLevel) : false;

    const recordTypeLevel = createRecordTypeIdentifier({
      objectName,
      recordTypeName,
      funnelId: '',
    });

    const isRecordTypeLevelMatch = recordTypeName
      ? idOfRecordTypeLevel.endsWith(recordTypeLevel)
      : false;

    return isFunnelLevelMatch || isRecordTypeLevelMatch;
  });
};

export const isConfigurationItemUsedByStep = (id: string) => (rule: ConfigurationItem) => {
  return rule.usedByLeadingFieldPicklist?.includes(id);
};
